<script>
const openInstances = new Set();
</script>

<script setup>
import { computed, nextTick, onBeforeUnmount, provide, readonly, ref, useTemplateRef } from "vue";

import { useFloatingPosition } from "./useFloatingPosition";

const props = defineProps({
  align: {
    type: String,
    default: "left",
    validator: (v) => ["right", "left"].includes(v),
  },
  disabled: { type: Boolean, default: false },
});
const emit = defineEmits(["open"]);

const isOpen = ref(false);
provide("dropdownOpen", readonly(isOpen));

const trigger = useTemplateRef("trigger");
const dropdownMenu = useTemplateRef("dropdownMenu");

useFloatingPosition(trigger, dropdownMenu, isOpen, {
  placement: computed(() => (props.align === "right" ? "bottom-end" : "bottom-start")),
});

function handleKey(event) {
  if (!isOpen.value) return;
  if (event.key === "Escape" || event.key === "Esc") {
    close();
    event.stopPropagation();
  } else if (event.key === "Tab" && event.type === "keydown") {
    close();
    event.stopPropagation();
  }
}

function clickedOutside(event) {
  if (dropdownMenu.value?.contains(event.target)) return;
  if (trigger.value?.contains(event.target)) return;
  // Don't refocus the trigger on a pointer dismiss: focus would jump back into
  // the row and keep `tr:focus-within` true, pinning reveal-on-hover controls
  // (e.g. RowActions) open. Esc/Tab still refocus — that path is ARIA-correct.
  close({ refocus: false });
}

function toggleDropdown() {
  if (props.disabled) return;
  isOpen.value ? close() : open();
}

const instance = { close };

function open() {
  openInstances.forEach((inst) => {
    if (inst !== instance) inst.close();
  });
  document.addEventListener("click", clickedOutside);
  document.addEventListener("keyup", handleKey);
  document.addEventListener("keydown", handleKey);
  isOpen.value = true;
  openInstances.add(instance);
  emit("open");
}

async function close({ refocus = true } = {}) {
  if (!isOpen.value) return;
  isOpen.value = false;
  removeListeners();
  openInstances.delete(instance);
  if (!refocus) return;
  await nextTick();
  // Re-focus the trigger after closing the dropdown
  // This should be called even if the dropdown is closed on tab,
  // so the next item in the original tab order is focused (since
  // the dropdown is teleported to <body>)
  trigger.value?.focus();
}

function removeListeners() {
  document.removeEventListener("click", clickedOutside);
  document.removeEventListener("keyup", handleKey);
  document.removeEventListener("keydown", handleKey);
}

onBeforeUnmount(() => {
  removeListeners();
  openInstances.delete(instance);
});
</script>

<template>
  <div class="dropdown">
    <button
      ref="trigger"
      type="button"
      aria-haspopup="menu"
      :aria-expanded="isOpen ? 'true' : 'false'"
      :aria-disabled="disabled || undefined"
      class="dropdown-trigger"
      :class="{ 'dropdown-disabled': disabled }"
      @click.stop="toggleDropdown"
    >
      <slot name="trigger" :is-open />
    </button>
    <Teleport to="body">
      <!-- eslint-disable-next-line vuejs-accessibility/click-events-have-key-events, vuejs-accessibility/no-static-element-interactions -->
      <div v-if="isOpen" ref="dropdownMenu" class="dropdown-menu" @click="close">
        <div v-keyboard-nav.ignore-trigger.roving-tabindex role="menu" class="dropdown-content">
          <slot />
        </div>
      </div>
    </Teleport>
  </div>
</template>

<style lang="scss" scoped>
@use "@/styles/layers";
@use "@/colors" as c;

.dropdown {
  display: inline-flex;

  .dropdown-trigger {
    display: inherit;
    cursor: pointer;
    outline: revert;
    background: none;
  }

  .dropdown-disabled {
    opacity: 0.4;
    cursor: default;
    pointer-events: none;
  }
}

.dropdown-menu {
  // Anchor the teleported menu to the viewport from the very first paint.
  // Without this, the menu briefly renders in static flow at the end of <body>
  // while floating-ui's computePosition() (async) is still pending. The
  // v-keyboard-nav directive auto-focuses the first item on mount, and
  // focusing an off-viewport element makes the browser scroll to it.
  // Floating-ui's inline-style updates still override these coordinates.
  position: fixed;
  top: 0;
  left: 0;
  z-index: layers.$floating;
  min-width: 16rem;

  .dropdown-content {
    background-color: c.$background-ui;
    box-shadow: 0 2px 16px rgb(5 0 56 / 12%);
    padding: 4px;
    border-radius: 8px;
    display: flex;
    flex-direction: column;
    gap: 2px;

    :deep(li),
    :deep(.list-item) {
      display: flex;
      align-items: center;
      height: 36px;
      padding: 0 8px;
      border-radius: 4px;
      cursor: pointer;
      font-size: 14px;
      color: c.$text;
      white-space: nowrap;
      list-style: none;

      &:hover,
      &:focus-visible {
        background-color: c.$background-grey-light;
        outline: none;
      }

      &:active {
        background-color: c.$background-alpha-active;
      }

      &:deep(.disabled) {
        cursor: not-allowed;
        color: c.$text-disabled;
        pointer-events: none;
      }
    }
  }
}
</style>
